import { useAtomValue } from "jotai";
import { useEffect, useState } from "react";
import { apiService } from "@/api/service";
import { availableAccountsAtom } from "@/store";
import type { AUTProperty } from "@/api/queries/account";

type PropertiesMap = Record<string, AUTProperty[]>;

export function useAccountProperties() {
  const accounts = useAtomValue(availableAccountsAtom);
  const [propertiesMap, setPropertiesMap] = useState<PropertiesMap>({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!accounts || accounts.length === 0) {
      setPropertiesMap({});
      return;
    }

    const loadProperties = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const results = await Promise.all(
          accounts.map(async (accountUser) => {
            try {
              const tree = await apiService.account.getAccountUserTree(accountUser.id);
              return {
                accountUserId: accountUser.id,
                properties: tree.account?.properties || [],
                //owner: tree.account?.owner,
              };
            } catch (err) {
              console.error(`Failed to load properties for account ${accountUser.id}:`, err);
              return {
                accountUserId: accountUser.id,
                properties: [],
                //owner: tree.account?.owner,
              };
            }
          })
        );

        const map: PropertiesMap = {};
        results.forEach(({ accountUserId, properties }) => {
          map[accountUserId] = properties;
        });

        setPropertiesMap(map);
      } catch (err) {
        const error = err instanceof Error ? err : new Error("Failed to load properties");
        setError(error);
        console.error("Failed to load properties:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadProperties();
  }, [accounts]);

  return {
    propertiesMap,
    isLoading,
    error,
    getProperties: (accountUserId: string) => propertiesMap[accountUserId] || [],
  };
}
